import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { Eye, EyeOff, CheckCircle, XCircle, Lock, Mail } from 'lucide-react';
import { loginWithApprovalToken } from '../../services/authService';
import { updateProfile } from '../../models/userModel';
import TextInput from '../../components/common/TextInput';
import Button from '../../components/common/Button';

const schema = yup.object().shape({
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[@$!%*?&#]/, 'Password must contain at least one special character'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
});

function PasswordSetup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [authenticating, setAuthenticating] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { control, handleSubmit, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    }
  });

  const password = watch('password');

  // Authenticate with token on mount
  useEffect(() => {
    async function authenticateToken() {
      if (!token) {
        toast.error('Invalid or missing password setup link');
        setAuthenticating(false);
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      try {
        console.log('🔐 Authenticating with token...');
        const response = await loginWithApprovalToken(token, 'setup-password');
        
        if (response.status === 1 && response.user) {
          console.log('✅ Authentication successful:', response.user);
          setUser(response.user);
          setAuthenticated(true);
          toast.success(`Welcome ${response.user.user_full_name}! Please set your password.`);
        } else {
          throw new Error('Authentication failed');
        }
      } catch (error) {
        console.error('❌ Token authentication failed:', error);
        toast.error(error.message || 'Invalid or expired password setup link');
        setTimeout(() => navigate('/login'), 2000);
      } finally {
        setAuthenticating(false);
      }
    }

    authenticateToken();
  }, [token, navigate]);

  const onSubmit = async (data) => {
    if (!user) {
      toast.error('User information not available');
      return;
    }

    setLoading(true);
    try {
      // Update password via profile update
      const response = await updateProfile({
        full_name: user.user_full_name,
        email: user.user_email,
        phone: user.user_phone || '',
        password: data.password,
        user_delegate: user.user_delegate || null,
      });

      if (response.status === 1) {
        toast.success('Password set successfully! Redirecting to dashboard...');
        
        // Clear the token from URL for security
        window.history.replaceState({}, document.title, '/dashboard');
        
        // Redirect to dashboard after brief delay
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      } else {
        throw new Error(response.message || 'Failed to set password');
      }
    } catch (error) {
      console.error('❌ Password setup failed:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  // Password strength indicator
  const getPasswordStrength = () => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[@$!%*?&#]/.test(password)) strength++;

    if (strength <= 2) return { strength: 1, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 4) return { strength: 2, label: 'Medium', color: 'bg-yellow-500' };
    if (strength <= 5) return { strength: 3, label: 'Strong', color: 'bg-green-500' };
    return { strength: 4, label: 'Very Strong', color: 'bg-green-600' };
  };

  const passwordStrength = getPasswordStrength();

  const requirements = [
    { met: password?.length >= 8, label: 'At least 8 characters' },
    { met: /[a-z]/.test(password || ''), label: 'One lowercase letter' },
    { met: /[A-Z]/.test(password || ''), label: 'One uppercase letter' },
    { met: /[0-9]/.test(password || ''), label: 'One number' },
    { met: /[@$!%*?&#]/.test(password || ''), label: 'One special character (@$!%*?&#)' },
  ];

  if (authenticating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Verifying your setup link...</p>
        </div>
      </div>
    );
  }

  if (!authenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-red-50">
        <div className="text-center p-8">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Invalid Link</h1>
          <p className="text-gray-600 mb-4">
            This password setup link is invalid or has expired.
          </p>
          <p className="text-sm text-gray-500">
            Redirecting to login page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50 p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <img src="/images/logo.png" alt="Silulumanzi Logo" className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Set Your Password</h1>
          <p className="text-gray-600">Welcome to Silulumanzi Portal</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* User Info */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-5 w-5 text-blue-600" />
              <span className="font-semibold text-gray-800">{user.user_full_name}</span>
            </div>
            <p className="text-sm text-gray-600 ml-7">{user.user_email}</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      className="pr-10"
                    />
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
              )}

              {/* Password Strength Indicator */}
              {password && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Password Strength:</span>
                    <span className={`text-xs font-semibold ${
                      passwordStrength.strength === 1 ? 'text-red-500' :
                      passwordStrength.strength === 2 ? 'text-yellow-500' :
                      'text-green-500'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${
                          level <= passwordStrength.strength
                            ? passwordStrength.color
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      className="pr-10"
                    />
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Password Requirements */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Lock size={16} />
                Password Requirements
              </h3>
              <ul className="space-y-2">
                {requirements.map((req, index) => (
                  <li key={index} className="flex items-center gap-2 text-xs">
                    {req.met ? (
                      <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle size={16} className="text-gray-300 flex-shrink-0" />
                    )}
                    <span className={req.met ? 'text-green-700' : 'text-gray-600'}>
                      {req.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              className="w-full justify-center py-3 text-base font-semibold"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Setting Password...
                </span>
              ) : (
                'Set Password & Continue'
              )}
            </Button>
          </form>

          {/* Help Text */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              After setting your password, you'll be automatically logged in.
              <br />
              You can use this password to log in from anywhere.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Need help? Contact IT Support
          </p>
          <p className="text-xs text-gray-400 mt-2">
            © {new Date().getFullYear()} Silulumanzi. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PasswordSetup;
